/**
 * matterSearch.js
 * ===============
 *
 * BM-H285. The single sanctioned way to search the matter list from the post
 * screens.
 *
 * Why this file exists. Every one of the seven matter picker call sites across
 * PostHub, PostIntakeQueue, OutgoingPostQueue and OutgoingPostDetail used to do
 * this:
 *
 *     try { setResults(res.data.results || []); } catch { setResults([]); }
 *
 * A failed read and a matter that does not exist therefore looked identical on
 * screen. A fee earner searching for a matter, seeing nothing, and concluding
 * the matter is not there is exactly how post gets filed against the wrong
 * matter or a duplicate matter folder gets created. The backend read is now
 * paged to exhaustion and raises rather than returning a short list, but that
 * only helps if the front end stops converting the raised failure back into an
 * empty result set. That is what this module is for.
 *
 * The contract: resolve with an array of matters, or throw. Never resolve with
 * an empty array to mean "something went wrong".
 */
import axios from "axios";
import { API } from "../config/api";

/**
 * Thrown when the matter list could not be read, as distinct from having been
 * read and found not to contain the search term. Carries a message written for
 * a fee earner rather than for a log.
 */
export class MatterSearchUnavailable extends Error {
  constructor(message, cause) {
    super(message);
    this.name = "MatterSearchUnavailable";
    this.cause = cause;
  }
}

/**
 * The message the UI shows. Deliberately says what is and is not known, and
 * tells the user not to file on the strength of an empty result.
 */
export const MATTER_SEARCH_UNAVAILABLE_MESSAGE =
  "The matter list could not be read, so this search cannot tell you whether " +
  "the matter exists. Do not file this item until the search works again.";

/**
 * Search the matter folders.
 *
 * @param {string} query search term, trimmed by the caller or here
 * @returns {Promise<Array>} the matching matters, possibly empty, which means
 *   the list was read in full and nothing matched
 * @throws {MatterSearchUnavailable} when the list could not be read at all
 */
export async function searchMatters(query) {
  const q = (query || "").trim();
  if (!q) return [];

  let response;
  try {
    response = await axios.get(
      `${API}/postintake/matters/search?q=${encodeURIComponent(q)}`
    );
  } catch (err) {
    throw new MatterSearchUnavailable(MATTER_SEARCH_UNAVAILABLE_MESSAGE, err);
  }

  const body = response && response.data ? response.data : {};

  // The endpoint used to answer HTTP 200 with {results: [], error: "..."} on a
  // failed read. It now raises a 503 instead, but an older backend may still be
  // serving during a rolling deploy, so an error field is still treated as a
  // failure rather than as an empty result.
  if (body.error) {
    throw new MatterSearchUnavailable(
      MATTER_SEARCH_UNAVAILABLE_MESSAGE,
      new Error(body.error)
    );
  }

  // A missing results key is not an empty list. An endpoint that answered
  // without the field it is contracted to return has not answered.
  if (!Array.isArray(body.results)) {
    throw new MatterSearchUnavailable(
      MATTER_SEARCH_UNAVAILABLE_MESSAGE,
      new Error("the matter search response carried no results array")
    );
  }

  return body.results;
}
